/**
 * ChannelConstants — mesaj/kanal alanındaki dağınık bit-yorumu ve raw
 * literal'lerin tek merkezi kaynağı. Kaynak `packages/constants/src/
 * ChannelConstants.ts` paritesi.
 *
 *   1. ChannelTypes — Kaynak NUMERIC (0/1/2/3...) enum kullanır. TEBHOS backend
 *      `tebhos_dto/src/channel.rs::kaynak_channel_type_code()` zaten bu numeric
 *      kodu üretiyor ve `ChannelResponse.channel_type` (i32) alanıyla HER
 *      response'ta (guild ChannelBrief dahil) taşıyor — FE tarafı artık aynı
 *      numeric enum'u birebir kullanır (önceki turda "bilinçli sapma" olarak
 *      korunan string-enum kararı bu turda GERİ ALINDI — backend zaten
 *      hizalıydı, FE'nin hizalanmaması gereksiz bir çeviri katmanı yaratıyordu).
 *
 *      DM/GROUP_DM ayrı bir model DEĞİL — Kaynak'da olduğu gibi tek `Channel`
 *      tipinin `type` alanı farklı bir değer alır (bkz. ChannelRecord.ts).
 *
 *      Backend/gateway'in henüz numeric'e geçmemiş bazı wire yolları (WS READY
 *      `private_channels`, eski cache) hâlâ eski string literal ("TEXT" vb.)
 *      gönderebilir — `normalizeChannelTypeCode()` bu ihtimale karşı wire
 *      sınırında (yalnız `lib/api/client.ts` gibi wire-parse noktalarında)
 *      kullanılan tek-seferlik bir köprü fonksiyonudur; domain modeli
 *      (ChannelRecord, ServerRecord vb.) SADECE numeric okur.
 *
 *   2. Permissions bitfield — Kaynak'da 54 sparse flag (bit'ler Discord-uyumlu)
 *      bu dosyadadır. Sprint [PERM-SPARSE] (2026-07-16) ÖNCESİ TEBHOS backend
 *      `permission/flags.rs` DENSE bir layout (0-35, ardışık) kullanıyordu ve
 *      bu iki sistem BİLİNÇLİ olarak farklı bit-uzaylarındaydı (backend
 *      `kaynak_perms_to_tebhos`/`tebhos_bits_to_kaynak` çeviri katmanlarıyla
 *      köprülüyordu). Bu sprint backend'i Kaynak'la BİREBİR sparse bit
 *      numaralarına remap etti (migrations/0129) — artık İKİ SİSTEM DE AYNI
 *      bit-uzayını paylaşıyor. Permissions YİNE DE burada TANIMLANMAZ —
 *      `features/permissions/utils/Flags.ts` (ES2017-güvenli `BigInt(1)`)
 *      tek kaynak olarak KALIR (bu dosyanın sorumluluğu message/channel-type
 *      sabitleri; permission flag'leri kendi feature klasöründe organize).
 *
 * MessageTypes/MessageFlags/MessageEmbedTypes/MessageStates ve attachment
 * flag'leri Kaynak'la birebir sayısal değerlerle port edilir (wire zaten bu
 * sayıları taşır — bkz. ChatMessageCreate.ts `message_type`, MessageRecord.ts
 * `flags & 4`, AnimatedMediaUtils.ts `1 << 5`).
 */

import type { ValueOf } from "./ValueOf";

/**
 * Kaynak `ChannelConstants.ts` ile birebir (packages/constants/src/
 * ChannelConstants.ts:5-13). `GUILD_LINK`/`DM_PERSONAL_NOTES` TEBHOS'ta henüz
 * FE-tüketilen bir özellik değil (backend Scylla katmanında
 * `CHANNEL_TYPE_DM_PERSONAL_NOTES=999` sabiti var — bkz. tebhos_scylla
 * channel_data.rs — ama bunu döndüren bir FE akışı yok); sabitler yine de
 * numeric-enum PARİTESİ için burada tutulur (Kaynak'daki her değer karşılığı
 * var — kalıntı yaratmadan tam liste).
 */
export const ChannelTypes = {
  GUILD_TEXT: 0,
  DM: 1,
  GUILD_VOICE: 2,
  GROUP_DM: 3,
  GUILD_CATEGORY: 4,
  GUILD_LINK: 998,
  DM_PERSONAL_NOTES: 999,
} as const;

export type ChannelType = ValueOf<typeof ChannelTypes>;

export const GUILD_TEXT_BASED_CHANNEL_TYPES = new Set<number>([ChannelTypes.GUILD_TEXT, ChannelTypes.GUILD_VOICE]);
export const TEXT_BASED_CHANNEL_TYPES = new Set<number>([
  ...GUILD_TEXT_BASED_CHANNEL_TYPES,
  ChannelTypes.DM,
  ChannelTypes.DM_PERSONAL_NOTES,
  ChannelTypes.GROUP_DM,
]);

/**
 * Legacy string kanal tipi literal'lerini (backend'in henüz numeric'e
 * geçmemiş bazı wire yolları — örn. WS READY `private_channels`, eski
 * cache satırları — hâlâ gönderebileceği "TEXT"/"AUDIO"/"DM"/"GROUP_DM"/
 * "CATEGORY") numeric `ChannelTypes` koduna çevirir. `kaynak_channel_type_code`
 * (tebhos_dto/src/channel.rs) ile birebir aynı eşleme tablosu.
 *
 * SADECE wire-parse sınırında (lib/api/client.ts, ChannelRecord/ServerRecord
 * normalize helper'ları) çağrılır — domain modelleri zaten numeric taşır,
 * tekrar normalize ETMEZ.
 */
export function normalizeChannelTypeCode(raw: unknown): ChannelType {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw as ChannelType;
  if (typeof raw === "string") {
    switch (raw.toUpperCase()) {
      case "DM":
        return ChannelTypes.DM;
      case "AUDIO":
      case "GUILD_VOICE":
      case "VOICE":
        return ChannelTypes.GUILD_VOICE;
      case "GROUP_DM":
        return ChannelTypes.GROUP_DM;
      case "CATEGORY":
      case "GUILD_CATEGORY":
        return ChannelTypes.GUILD_CATEGORY;
      case "TEXT":
      case "GUILD_TEXT":
        return ChannelTypes.GUILD_TEXT;
      default:
        return ChannelTypes.GUILD_TEXT;
    }
  }
  return ChannelTypes.GUILD_TEXT;
}

/**
 * Wire kanalının sayısal tipini çözer. API iki ayrı şekil yayar:
 *   - sunucu kanalı: `type` SAYISAL (kaynak-referans), `channel_type` DİZE etiket;
 *   - DM / grup-DM:  `type` DİZE ("DM"), `channel_type` SAYISAL.
 * Bu yüzden hangisi sayıysa o kazanır (önce `type`). Eskiden `channel_type`
 * koşulsuz tercih ediliyordu → sunucu kanalının etiketi tanınmayınca (ör. link
 * kanalı) tip sessizce GUILD_TEXT oluyordu.
 */
export function resolveWireChannelType(channel: { type?: unknown; channel_type?: unknown }): ChannelType {
  if (typeof channel.type === "number" && Number.isFinite(channel.type)) return channel.type as ChannelType;
  if (typeof channel.channel_type === "number" && Number.isFinite(channel.channel_type)) {
    return channel.channel_type as ChannelType;
  }
  return normalizeChannelTypeCode(channel.type ?? channel.channel_type);
}

/**
 * Ters yön: numeric `ChannelTypes` → backend `CreateChannelRequest`/
 * `UpdateChannelRequest` gövdesinin beklediği string literal ("TEXT"|"AUDIO").
 * Bu iki request DTO'su (tebhos_dto/src/channel.rs) bilerek string kalır —
 * guild kanal CRUD'u TEXT/AUDIO dışında bir tip kabul etmez, backend'i
 * numeric'e çevirmek bu turun kapsamı dışında (yalnız FE-model hizası).
 */
export function channelTypeToWireString(type: ChannelType): "TEXT" | "AUDIO" {
  return type === ChannelTypes.GUILD_VOICE ? "AUDIO" : "TEXT";
}

/** Mesaj tipi (wire alanı `message_type`, sayısal). Kaynak paritesi. */
export const MessageTypes = {
  DEFAULT: 0,
  RECIPIENT_ADD: 1,
  RECIPIENT_REMOVE: 2,
  CALL: 3,
  CHANNEL_NAME_CHANGE: 4,
  CHANNEL_ICON_CHANGE: 5,
  CHANNEL_PINNED_MESSAGE: 6,
  USER_JOIN: 7,
  REPLY: 19,
  CLIENT_SYSTEM: 99,
} as const;

export type MessageTypeValue = ValueOf<typeof MessageTypes>;

/** Mesaj referans tipi (reply vs forward). Kaynak paritesi. */
export const MessageReferenceTypes = {
  DEFAULT: 0,
  FORWARD: 1,
} as const;

export type MessageReferenceTypeValue = ValueOf<typeof MessageReferenceTypes>;

/**
 * Mesaj bayrakları (bitfield, sayısal). `flags & MessageFlags.SUPPRESS_EMBEDS`
 * kalıbı zaten TEBHOS'ta yaygın (`flags & 4`) — bu enum eski raw değerlerle
 * BİREBİR uyumludur (SUPPRESS_EMBEDS === 4).
 */
export const MessageFlags = {
  SUPPRESS_EMBEDS: 1 << 2,
  SUPPRESS_NOTIFICATIONS: 1 << 12,
  VOICE_MESSAGE: 1 << 13,
  COMPACT_ATTACHMENTS: 1 << 17,
} as const;

export type MessageFlagValue = ValueOf<typeof MessageFlags>;

/** Kullanıcının gönderebileceği (client-set edilebilir) mesaj bayrakları. */
export const SENDABLE_MESSAGE_FLAGS =
  MessageFlags.SUPPRESS_EMBEDS |
  MessageFlags.SUPPRESS_NOTIFICATIONS |
  MessageFlags.COMPACT_ATTACHMENTS |
  MessageFlags.VOICE_MESSAGE;

/**
 * Attachment bayrakları (bitfield, sayısal). IS_ANIMATED === 1<<5 === 32,
 * TEBHOS'taki mevcut `ATTACHMENT_IS_ANIMATED_FLAG = 1 << 5` ile BİREBİR uyumlu.
 *
 * SPOILER-BİT DÜZELTMESİ (2026-08-03): IS_SPOILER artık kaynak-referans wire
 * sözleşmesiyle AYNI — 1<<3 (openapi "IS_SPOILER value 8"). Eski "bit 0
 * backend-gerçeği" sapması KAPATILDI: backend de 1<<3'e çekildi
 * (`tebhos_api/src/attachment/controller.rs` ATTACHMENT_FLAG_SPOILER ve
 * message/controller.rs bind + attachments[]→spoiler_attachment_ids köprüsü).
 * İki farklı sabit (composer `@tebhos/constants` 1<<3 vs render burası 1<<0)
 * spoiler bayrağının wire'da kaybolmasının KÖK NEDENİydi — tek doğru: 1<<3.
 */
export const MessageAttachmentFlags = {
  IS_SPOILER: 1 << 3,
  CONTAINS_EXPLICIT_MEDIA: 1 << 4,
  IS_ANIMATED: 1 << 5,
} as const;

export type MessageAttachmentFlagValue = ValueOf<typeof MessageAttachmentFlags>;

/** Embed medya bayrakları (bitfield, sayısal). Kaynak paritesi. */
export const EmbedMediaFlags = {
  CONTAINS_EXPLICIT_MEDIA: 1 << 4,
  IS_ANIMATED: 1 << 5,
} as const;

export type EmbedMediaFlagValue = ValueOf<typeof EmbedMediaFlags>;

/** Embed tipi (string). Kaynak paritesi. */
export const MessageEmbedTypes = {
  RICH: "rich",
  ARTICLE: "article",
  LINK: "link",
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
  GIFV: "gifv",
  BLUESKY: "bluesky",
} as const;

export type MessageEmbedType = ValueOf<typeof MessageEmbedTypes>;

/**
 * Optimistic mesaj gönderim durumu. Kaynak paritesi (EDITING dahil süperküme).
 *
 * NOT: `features/messaging/state/messages/Constants.ts` içinde EDITING'siz bir
 * yerel `MessageStates` (SENDING/SENT/FAILED) hâlâ VAR — buradaki merkezi tanım
 * onun bir SÜPERKÜMESİDİR (mevcut üç değer birebir korunur, sadece EDITING
 * eklenir). Tüketicilerin buna geçişi ayrı FAZ'da yapılır; bu faz yalnız kök.
 */
export const MessageStates = {
  SENT: "SENT",
  SENDING: "SENDING",
  EDITING: "EDITING",
  FAILED: "FAILED",
} as const;

export type MessageState = ValueOf<typeof MessageStates>;
